// TERRA ground material — MeshStandardMaterial + onBeforeCompile injection, so the planet-grade
// procedural texturework rides the standard pipeline (sun light, shadows, fog, ACES) for free.
// ONE static program (per-battle look varies only by uniforms — no per-battle shader compiles):
//  • fine-grain analytic normal perturbation (the planet shader's "not glassy" win),
//  • per-style height-band albedo detail + mottling over the baked vertex-color macro palette,
//  • ONE worley-edge call for dry cracks / ice veins (cosmetic; gameplay hazards are BAKED into
//    the aGlow vertex attribute so visuals always match the field data),
//  • lava/crevasse emissive from aGlow (pushed hot — the battle renderer has no bloom pass),
//  • built-pad deck plating from aPad, drifting cloud shadows from one scrolled fbm.

import * as THREE from "three";
import { CLOUD_COMMON, cloudSlabY, type CloudParams } from "../env/clouds";

export interface TerraMaterialHandle {
  material: THREE.MeshStandardMaterial;
  /** Drive per-frame animation (lava pulse, strata). Safe before compile. */
  setTime(t: number): void;
  /** Per-frame cloud drift (world-XZ studs) — feed the SAME offset the sky dome uses so the ground
   *  shadows track the clouds overhead. */
  setCloudWind(x: number, z: number): void;
  /** Point the cloud-shadow projection at the real sun. Safe before compile. */
  setSun(dir: THREE.Vector3): void;
  /** Set a live-tunable uniform by name (e.g. from the DEV panel). Safe before compile. No-op if unknown. */
  setUniform(name: string, value: number): void;
}

export const GLSL_NOISE = /* glsl */ `
  float th12(vec2 p){ vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
  float tvn(vec2 p){
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(th12(i), th12(i + vec2(1.0, 0.0)), u.x), mix(th12(i + vec2(0.0, 1.0)), th12(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  float tfbm(vec2 p){ return (tvn(p) * 0.5 + tvn(p * 2.03) * 0.25 + tvn(p * 4.09) * 0.125) / 0.875; }
  float twedge(vec2 p){
    vec2 i = floor(p), f = fract(p);
    float f1 = 8.0, f2 = 8.0;
    for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++){
      vec2 g = vec2(float(x), float(y));
      vec2 o = vec2(th12(i + g), th12(i + g + 19.7));
      vec2 r = g + o - f;
      float d = dot(r, r);
      if (d < f1){ f2 = f1; f1 = d; } else if (d < f2){ f2 = d; }
    }
    return sqrt(f2) - sqrt(f1);
  }
  // normal-perturbation height with a per-OCTAVE LOD weight (f.x=~26-stud … f.w=~5-stud grain), so each
  // octave can fade to its OWN wavelength. With f = vec4(1) this is exactly tfbm(p)*0.75 + tvn(p*5.1)*0.25.
  float terraBump(vec2 p, vec4 f){
    float b = (tvn(p) * 0.5 * f.x + tvn(p * 2.03) * 0.25 * f.y + tvn(p * 4.09) * 0.125 * f.z) / 0.875;
    return b * 0.75 + tvn(p * 5.1) * 0.25 * f.w;
  }
`;

export function terraMaterial(
  style: number,
  seed: number,
  center: THREE.Vector2,
  deckPhase: THREE.Vector2,
  cloud?: CloudParams,
  sun?: THREE.Vector3,
  seaY?: number,
): TerraMaterialHandle {
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true, // macro palette baked per vertex (JS-side banding — matches the field data)
    roughness: 0.94,
    metalness: 0,
  });
  const uniforms = {
    uTime: { value: 0 },
    uStyle: { value: style },
    uSeed: { value: (seed % 4096) * 0.37 },
    uCenter: { value: center },
    // Corner of the BUILD GRID in world space. The deck plating is drawn on a 100-stud lattice, and
    // without this it was phased to uCenter — so its seams sat ~67 studs off every tile boundary and
    // the plate grid visibly disagreed with the grid you build on. Phase it to the grid's own corner.
    uDeckPhase: { value: deckPhase },
    // --- live-tunable detail LOD knobs (DEV-panel-driven; these defaults = the values dialed in 2026-07-15:
    // flat-but-texturally-busy — strong fine grain/mottling/cracks, minimal relief, detail only up close) ---
    uGrainStrength: { value: 3 },      // overall micro-texture (albedo grain) amplitude
    uGrainReach: { value: 0.26 },      // <1 = grain fades sooner (detail concentrated close to the camera)
    uReliefStrength: { value: 0.1 },   // micro-relief (fine bump) amplitude
    uBaseReliefReach: { value: 2.2 },  // >1 = base surface relief persists farther before smoothing
    uFarSmoothStart: { value: 33 },    // px where the macro mottling starts fading toward smooth
    uFarSmoothEnd: { value: 73 },      // px where far ground is fully smoothed
    uFarFloor: { value: 0 },           // residual mottling kept at max distance (0 = dead flat)
    uMicroScale: { value: 6 },         // MAX RESOLUTION: >1 makes the grain finer (smaller features)
    uBaseReliefStrength: { value: 0.22 }, // base surface-relief (normal perturbation) amplitude
    uMottleStrength: { value: 3 },     // macro mottling contrast
    uCrackStrength: { value: 3 },      // dry-crack / ice-vein strength
    uSeaY: { value: Number.isFinite(seaY) ? (seaY as number) : 0 }, // garden waterline (per-pixel shoreline)
    uChasmDark: { value: 0.85 },       // crevasse/pit interior darkness (the depth cue that makes gaps READ)
    uWallShade: { value: 1 },          // steep-face darkening: walls read as rock/ice faces, not tilted floor
    uDebugLod: { value: 0 },          // 1 = paint the raw pixel-footprint as LOD color bands (dev viz)
    // cloud shadows (share the sky's coverage field; 0 shadowK ⇒ effectively off)
    uSunDir: { value: (sun ? sun.clone() : new THREE.Vector3(0.4, 0.7, 0.5)).normalize() },
    uCloudCoverage: { value: cloud?.coverage ?? 0.4 },
    uCloudScale: { value: cloud?.scale ?? 3200 },
    uCloudSlabY: { value: cloud ? cloudSlabY(cloud) : 3400 },
    uCloudShadowK: { value: cloud?.shadowK ?? 0 },
    uCloudWind: { value: new THREE.Vector2() },
    uEvolve: { value: 1 }, // churn-rate multiplier — kept in lockstep with the sky so shadows track morph
  };

  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);

    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>
        attribute float aGlow; attribute float aPad;
        varying float vGlow; varying float vPad; varying vec3 vTerraPos;`)
      .replace("#include <begin_vertex>", `#include <begin_vertex>
        vGlow = aGlow; vPad = aPad;
        vTerraPos = (modelMatrix * vec4(transformed, 1.0)).xyz;`);

    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>
        uniform float uTime; uniform float uStyle; uniform float uSeed; uniform vec2 uCenter; uniform vec2 uDeckPhase;
        uniform vec3 uSunDir; uniform float uCloudCoverage, uCloudScale, uCloudSlabY, uCloudShadowK;
        uniform vec2 uCloudWind; uniform float uEvolve;
        uniform float uGrainStrength, uGrainReach, uReliefStrength, uBaseReliefReach;
        uniform float uFarSmoothStart, uFarSmoothEnd, uFarFloor;
        uniform float uMicroScale, uBaseReliefStrength, uMottleStrength, uCrackStrength, uDebugLod;
        uniform float uSeaY, uChasmDark, uWallShade;
        varying float vGlow; varying float vPad; varying vec3 vTerraPos;
        ${GLSL_NOISE}
        ${CLOUD_COMMON}`)
      // ---- fine-grain normal perturbation (analytic finite difference, world XZ) ----
      .replace("#include <normal_fragment_begin>", `#include <normal_fragment_begin>
        {
          // PROCEDURAL detail LOD: fwidth gives the world studs this pixel spans (its screen footprint).
          // With no bitmap there's no mip chain — instead we fade noise octaves as they'd fall below a pixel,
          // so the fine "crunch" stays crisp up close but flattens toward the horizon instead of shimmering
          // (the sparkle a mipmap would remove), and its cost drops on far/small fragments.
          float px = max(length(fwidth(vTerraPos.xz)), 1e-3);
          // PER-OCTAVE detail LOD: fade each grain as ITS OWN wavelength nears a pixel, so distant ground
          // smooths instead of shimmering while the close-up crunch is untouched (all weights = 1 near camera).
          float bpx = px / uBaseReliefReach;
          vec4 fw = vec4(
            1.0 - smoothstep(14.0, 30.0, bpx),  // ~26-stud — pulled in so far relief smooths out sooner
            1.0 - smoothstep(8.0, 20.0, bpx),   // ~13-stud
            1.0 - smoothstep(4.5, 13.0, bpx),   // ~6.4-stud
            1.0 - smoothstep(3.0, 11.0, bpx));  // ~5-stud
          vec2 tp = (vTerraPos.xz - uCenter) * (1.0 / 26.0) + uSeed;
          float e = 0.35;
          float f0 = terraBump(tp, fw);
          float fx = terraBump(tp + vec2(e, 0.0), fw);
          float fz = terraBump(tp + vec2(0.0, e), fw);
          float amp = mix(3.4, 1.1, vPad) * uBaseReliefStrength; // built decks are machined-smooth
          vec3 dNw = vec3(-(fx - f0), 0.0, -(fz - f0)) * amp;
          // (viewMatrix·dNw) is already length-preserving (rotation), so add it directly — and it's NaN-safe
          // when the perturbation fades to zero at distance (the old normalize()·length() dance would NaN).
          normal = normalize(normal + (viewMatrix * vec4(dNw, 0.0)).xyz);
          // close-range MICRO-RELIEF: fine ~1-2.4-stud bumps with a SMALL finite-difference step (the main
          // perturb's e≈9 studs can't resolve these). Reads as real surface texture under the sun when you
          // stand on the ground / look straight down; fades in only very close so it never shimmers at range.
          // ONE ~1.6-stud octave (no finer sub-octave to alias), a FD step (0.8 stud) SMALLER than the
          // wavelength (coherent gradient), a gentle gain (the old *7 gave ~45° tilts — a coarse bump, not
          // micro-relief), and a fade that completes near its Nyquist (px≈0.8). Softer on glossy ICE slicks.
          float mr = (1.0 - smoothstep(1.4, 3.4, px)) * (1.0 - vPad);
          if (mr > 0.001) {
            vec2 mp = (vTerraPos.xz - uCenter) * (1.0 / 1.8) + uSeed * 5.0;
            float me = 0.45;
            float m0 = tvn(mp);
            float mx = tvn(mp + vec2(me, 0.0));
            float mz = tvn(mp + vec2(0.0, me));
            float rgain = uStyle > 2.5 ? 1.2 : 1.9;
            vec3 mN = vec3(-(mx - m0), 0.0, -(mz - m0)) * (mr * rgain * uReliefStrength);
            normal = normalize(normal + (viewMatrix * vec4(mN, 0.0)).xyz);
          }
          // ---- steep-face (wall) shading, all biomes: darken + cool faces as they tip off vertical, so
          // crevasse walls / channel banks / mesa flanks read as FACES rather than oddly-tinted floor.
          // Runs HERE (after color_fragment) because 'normal' only exists from normal_fragment_begin on;
          // diffuseColor is already declared upstream, so tinting it here feeds the lighting as usual.
          {
            float upness = clamp(dot(normal, normalize((viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz)), 0.0, 1.0);
            float wallF = smoothstep(0.35, 0.8, 1.0 - upness) * uWallShade * (1.0 - vPad);
            diffuseColor.rgb *= 1.0 - wallF * 0.34;
            diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.88, 0.95, 1.08), wallF); // cool cast
          }
        }`)
      // ---- albedo detail: mottling + height micro-banding + cracks + pad plating + cloud shadows ----
      .replace("#include <color_fragment>", `#include <color_fragment>
        {
          vec2 wp = vTerraPos.xz - uCenter;
          float px = max(length(fwidth(vTerraPos.xz)), 1e-3); // world studs per pixel (screen footprint)
          if (uDebugLod > 0.5) {
            vec3 dbg;
            if (uDebugLod < 1.5) {
              // MODE 1 — raw pixel FOOTPRINT bands (blue near/high-res … red/purple far/low-res). The input
              // the px-threshold sliders key off; independent of the sliders (by design).
              float d = px;
              if (d < 0.5) dbg = vec3(0.1, 0.1, 0.5); else if (d < 1.0) dbg = vec3(0.0, 0.4, 1.0);
              else if (d < 2.0) dbg = vec3(0.0, 1.0, 1.0); else if (d < 4.0) dbg = vec3(0.0, 1.0, 0.0);
              else if (d < 8.0) dbg = vec3(1.0, 1.0, 0.0); else if (d < 16.0) dbg = vec3(1.0, 0.5, 0.0);
              else if (d < 32.0) dbg = vec3(1.0, 0.0, 0.0); else dbg = vec3(0.5, 0.0, 0.5);
            } else {
              // MODE 2 — EFFECTIVE detail level: how much detail actually survives here after reach + far-
              // smoothing. RESPONDS to the LOD sliders (detail reach, base relief reach, far smooth start/end,
              // far detail floor). dark blue = smooth/low-detail, orange→white = full detail.
              float grainW = 1.0 - smoothstep(9.0, 20.0, px / uGrainReach);
              float baseW  = 1.0 - smoothstep(14.0, 30.0, px / uBaseReliefReach);
              float mottW  = uFarFloor + (1.0 - uFarFloor) * (1.0 - smoothstep(uFarSmoothStart, uFarSmoothEnd, px));
              float detail = clamp(grainW * 0.4 + baseW * 0.3 + mottW * 0.3, 0.0, 1.0);
              dbg = detail < 0.5 ? mix(vec3(0.04, 0.04, 0.16), vec3(0.95, 0.55, 0.0), detail * 2.0)
                                 : mix(vec3(0.95, 0.55, 0.0), vec3(0.96, 1.0, 0.92), (detail - 0.5) * 2.0);
            }
            gl_FragColor = vec4(dbg, 1.0); return;
          }
          // ---- crisp per-pixel MESO FEATURES (fix 1) — vGlow is the interpolated PRE-threshold feature
          // field (lava channels / crevasse intensity), so thresholding it HERE follows the true iso-contour
          // at any angle: the diagonal/curved edge stays sharp instead of staircasing to the 50-stud grid.
          // Baked BEFORE mottling/grain so the natural texture still rides over the feature. ----
          if (uStyle > 1.5 && uStyle < 2.5) {                         // SCORCHED: molten channels + pools
            float lava = smoothstep(0.12, 0.30, vGlow);
            diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.05, 0.025, 0.02), lava);
          } else if (uStyle > 2.5) {                                  // ICE: crevasses — wall band, then DEPTH
            // two-stage: rim→wall picks up glacial blue, interior falls toward near-black — the darkness
            // gradient is the depth cue that makes a gap READ as a void instead of tinted ground.
            float crevW = smoothstep(0.22, 0.5, vGlow);
            float crevD = smoothstep(0.45, 0.72, vGlow);
            diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.18, 0.30, 0.46), crevW);
            diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.02, 0.05, 0.11), crevD * uChasmDark);
          } else if (uStyle < 0.5) {                                  // GARDEN: shoreline from interpolated height
            float wet = smoothstep(uSeaY + 3.0, uSeaY - 6.0, vTerraPos.y);
            diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.10, 0.20, 0.20), wet);
          }
          vec2 mp = wp * (1.0 / 340.0) + uSeed;
          // fade the macro mottling toward its mean in the far field so distant ground reads SMOOTH (drops
          // resolution with distance) instead of carrying the same patchiness all the way to the horizon.
          float farFade = uFarFloor + (1.0 - uFarFloor) * (1.0 - smoothstep(uFarSmoothStart, uFarSmoothEnd, px));
          // PERF: the fbm was computed EVERY pixel then faded — skip it once it's faded to ~mean (like the grain/
          // crack blocks below). Where farFade≈0 the result is 0.5 regardless, so this saves the noise for free.
          float mott = 0.5;
          if (farFade > 0.02) mott = tfbm(mp);                        // regional mottling
          diffuseColor.rgb *= 0.82 + 0.36 * (0.5 + (mix(0.5, mott, farFade) - 0.5) * uMottleStrength);
          // close-range MICRO-TEXTURE: fine grain (~1-7 stud octaves) that resolves as the ground fills the
          // screen — so the first-person / look-straight-down view has real surface texture instead of a
          // smooth blur. Each octave is centered (n-0.5) and faded at ITS OWN wavelength, so nothing shimmers
          // and faded octaves add zero; the whole block is skipped past a few studs/pixel (free at range).
          float gpx = px / uGrainReach; // >1 reach ⇒ octaves stay resolved farther out (detail reaches farther)
          if (gpx < 22.0) {
            // procedural "resolution" tracks the pixel footprint: each octave stays at full strength until a
            // pixel spans SEVERAL of its features, then fades — so the COARSER octaves reach well into the
            // mid-field (detail no longer dies right at your feet) while the finer ones fill in up close.
            // Fades are generous on purpose: mild sub-pixel shimmer is acceptable and just reads as texture.
            vec2 gp = (wp + uSeed * 3.0) * uMicroScale; // uMicroScale>1 ⇒ finer features (higher max resolution)
            float grain = (tvn(gp * (1.0 / 15.0)) - 0.5) * 0.26 * (1.0 - smoothstep(9.0, 20.0, gpx))
                        + (tvn(gp * (1.0 / 6.0))  - 0.5) * 0.28 * (1.0 - smoothstep(3.5, 11.0, gpx))
                        + (tvn(gp * (1.0 / 2.2))  - 0.5) * 0.24 * (1.0 - smoothstep(1.2, 5.0, gpx))
                        + (tvn(gp * (1.0 / 0.9))  - 0.5) * 0.16 * (1.0 - smoothstep(0.4, 1.8, gpx));
            diffuseColor.rgb *= 1.0 + grain * uGrainStrength;
          }
          float band = tvn(vec2(vTerraPos.y * 0.021, uSeed * 7.0));   // subtle strata by height
          diffuseColor.rgb *= 0.94 + 0.12 * band;
          // cracks/veins are the priciest layer (a 9-cell worley loop) and alias at distance: fade AND skip
          // the whole call once a pixel spans more than a vein. Keyed to the ~10-stud vein WIDTH (not the
          // 210-stud pattern period) so the fade tracks when the vein actually goes sub-pixel — no shimmer.
          float crackLod = 1.0 - smoothstep(7.0, 20.0, px);
          if (crackLod > 0.01) {
            float crack = (1.0 - smoothstep(0.0, 0.09, twedge(wp * (1.0 / 210.0) + uSeed))) * crackLod;
            if (uStyle > 2.5) {                                        // ICE: blue fracture veins
              diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.30, 0.52, 0.72), crack * 0.55 * uCrackStrength);
            } else if (uStyle > 0.5) {                                 // ROCKY/SCORCHED: dark dry cracks
              diffuseColor.rgb *= 1.0 - crack * 0.38 * uCrackStrength;
            }
          }
          // built decks: plate grid + seams override the natural ground
          if (vPad > 0.03) {
            vec2 g = abs(fract((vTerraPos.xz - uDeckPhase) * (1.0 / 100.0)) - 0.5); // phased to the build grid
            float seam = smoothstep(0.46, 0.5, max(g.x, g.y));
            vec3 plate = vec3(0.30, 0.33, 0.37) * (0.9 + 0.2 * tvn(wp * 0.11));
            diffuseColor.rgb = mix(diffuseColor.rgb, plate * (1.0 - seam * 0.45), vPad);
          }
          // cloud shadows: walk the ground point UP the sun ray to the cloud slab and sample the very
          // same coverage field the sky raymarches — the shadows always line up with the clouds overhead.
          if (uCloudShadowK > 0.001) {
            vec2 projXZ = vTerraPos.xz + uSunDir.xz * ((uCloudSlabY - vTerraPos.y) / max(uSunDir.y, 0.15));
            float cov = cloudCover(projXZ, uCloudCoverage, uCloudScale, uCloudWind, uTime * uEvolve);
            diffuseColor.rgb *= 1.0 - uCloudShadowK * smoothstep(0.05, 0.55, cov);
          }
        }`)
      // ---- roughness: ice/slicks glassy, decks machined ----
      .replace("#include <roughnessmap_fragment>", `#include <roughnessmap_fragment>
        if (uStyle > 2.5) roughnessFactor = mix(0.45, roughnessFactor, smoothstep(184.0, 205.0, vTerraPos.y));
        roughnessFactor = mix(roughnessFactor, 0.55, vPad);`)
      // ---- emissive: molten channels / cold crevasse glow (baked aGlow == the gameplay hazard) ----
      .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
        if (vGlow > 0.01) {
          vec2 wp = vTerraPos.xz - uCenter;
          float pulse = 0.8 + 0.2 * sin(uTime * 1.7 + tvn(wp * 0.01) * 6.28);
          float micro = 0.65 + 0.7 * tfbm(wp * (1.0 / 90.0) + uSeed * 3.0);
          vec3 glowCol = uStyle > 2.5 ? vec3(0.10, 0.32, 0.55) : vec3(1.0, 0.30, 0.06);
          float boost = uStyle > 2.5 ? 0.8 : 2.6;                      // lava pushed hot (no bloom pass here)
          totalEmissiveRadiance += glowCol * (vGlow * vGlow) * micro * pulse * boost;
        }`);

    mat.userData.shader = shader;
  };

  return {
    material: mat,
    setTime(t: number): void {
      uniforms.uTime.value = t;
    },
    setCloudWind(x: number, z: number): void {
      (uniforms.uCloudWind.value as THREE.Vector2).set(x, z);
    },
    setSun(dir: THREE.Vector3): void {
      (uniforms.uSunDir.value as THREE.Vector3).copy(dir).normalize();
    },
    setUniform(name: string, value: number): void {
      const u = (uniforms as Record<string, { value: unknown }>)[name];
      if (u && typeof u.value === "number") u.value = value;
    },
  };
}
